import React, { useEffect, useRef } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import {
  fetchSystemHealth,
  selectSystemHealth,
  selectHealthLoading,
  selectHealthError,
  selectOverallStatus,
} from '../store/systemHealthSlice';
import {
  MODULE_LABELS,
  statusColor,
  statusIconColor,
  statusLabel,
} from '../services/systemHealthService';
import type { ServiceStatus, ProviderHealth, ModuleHealth } from '../services/systemHealthService';
import { typography } from '~/theme';

interface SystemStatusIndicatorProps {
  /** Compact mode shows module dots + tooltip; expanded shows collapsible provider detail */
  variant?: 'compact' | 'expanded';
  /** Polling interval in ms (0 = no polling, fetch once) */
  pollInterval?: number;
}

// ── Status icon helper ───────────────────────────────────────────────

const StatusIcon: React.FC<{ status: ServiceStatus; size?: number }> = ({ status, size = 14 }) => {
  const sx = { fontSize: size };
  switch (status) {
    case 'healthy': return <CheckCircleIcon color="success" sx={sx} />;
    case 'degraded': return <WarningAmberIcon color="warning" sx={sx} />;
    case 'down': return <ErrorIcon color="error" sx={sx} />;
    case 'not_configured': return <RemoveCircleOutlineIcon color="disabled" sx={sx} />;
    default: return <RemoveCircleOutlineIcon color="disabled" sx={sx} />;
  }
};

// ── Compact module dot (for dashboard / login) ───────────────────────

const ModuleDot: React.FC<{ moduleKey: string; module: ModuleHealth }> = ({ moduleKey, module }) => {
  const label = MODULE_LABELS[moduleKey] ?? moduleKey;
  const activeLabel = module.active_provider ? `Active: ${module.active_provider}` : '';
  const providerLines = module.providers.map((p: ProviderHealth) => {
    const lat = p.latency_ms ? ` (${Math.round(p.latency_ms)}ms)` : '';
    return `${p.name}: ${statusLabel(p.status)}${lat}`;
  });
  const tip = [label, activeLabel, ...providerLines, module.warning].filter(Boolean).join('\n');

  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line', fontSize: typography.caption.fontSize }}>{tip}</span>} arrow>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
        <FiberManualRecordIcon color={statusIconColor(module.status)} sx={{ fontSize: 10 }} />
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ── Expanded module section (for admin monitor) ──────────────────────

const ModuleSection: React.FC<{ moduleKey: string; module: ModuleHealth }> = ({ moduleKey, module }) => {
  const [open, setOpen] = React.useState(true);
  const label = MODULE_LABELS[moduleKey] ?? moduleKey;

  return (
    <Box sx={{ mb: 1 }}>
      {/* Module header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          py: 0.5,
          '&:hover': { bgcolor: 'action.hover' },
          borderRadius: 1,
          px: 0.5,
        }}
        onClick={() => setOpen(!open)}
      >
        <StatusIcon status={module.status} size={16} />
        <Typography variant="body2" fontWeight={typography.label.fontWeight} sx={{ flex: 1 }}>
          {label}
        </Typography>
        <Chip
          label={statusLabel(module.status)}
          size="small"
          color={statusColor(module.status)}
          sx={{ height: 20, fontSize: typography.caption.fontSize }}
        />
        {module.active_provider && (
          <Chip
            label={`Active: ${module.active_provider}`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ height: 20, fontSize: typography.caption.fontSize }}
          />
        )}
        <IconButton size="small" sx={{ p: 0 }}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Provider rows */}
      <Collapse in={open}>
        <Box sx={{ pl: 3, pt: 0.5 }}>
          {module.providers.map((p: ProviderHealth) => (
            <Box
              key={p.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <StatusIcon status={p.status} size={14} />
              <Typography variant="body2" sx={{ minWidth: 120 }}>
                {p.name}
              </Typography>
              {/* Configured */}
              <Tooltip title="Configured" arrow>
                <Chip
                  label={p.configured ? 'Yes' : 'No'}
                  size="small"
                  variant="outlined"
                  color={p.configured ? 'success' : 'default'}
                  sx={{ height: 18, fontSize: typography.overline.fontSize, minWidth: 36 }}
                />
              </Tooltip>
              {/* Reachable */}
              <Tooltip title="Reachable" arrow>
                <Chip
                  label={p.reachable ? 'Yes' : 'No'}
                  size="small"
                  variant="outlined"
                  color={p.reachable ? 'success' : p.configured ? 'error' : 'default'}
                  sx={{ height: 18, fontSize: typography.overline.fontSize, minWidth: 36 }}
                />
              </Tooltip>
              {/* Active */}
              {p.active && (
                <Chip label="ACTIVE" size="small" color="primary" sx={{ height: 18, fontSize: typography.overline.fontSize, fontWeight: typography.h1.fontWeight }} />
              )}
              {/* Latency */}
              {p.latency_ms != null && (
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50, textAlign: 'right' }}>
                  {Math.round(p.latency_ms)}ms
                </Typography>
              )}
              {/* Last checked */}
              {p.last_check_timestamp && (
                <Typography variant="caption" color="text.disabled" noWrap>
                  {new Date(p.last_check_timestamp).toLocaleTimeString()}
                </Typography>
              )}
              {/* Error */}
              {p.error_message && (
                <Tooltip title={p.error_message} arrow>
                  <Typography variant="caption" color="error" noWrap sx={{ maxWidth: 200 }}>
                    {p.error_message}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          ))}
          {module.warning && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption" color="warning.main">
                ⚠ {module.warning}
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ── Main component ───────────────────────────────────────────────────

const SystemStatusIndicator: React.FC<SystemStatusIndicatorProps> = ({
  variant = 'compact',
  pollInterval = 0,
}) => {
  const dispatch = useAppDispatch();
  const health = useAppSelector(selectSystemHealth);
  const loading = useAppSelector(selectHealthLoading);
  const error = useAppSelector(selectHealthError);
  const overall = useAppSelector(selectOverallStatus);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Initial fetch + polling
  useEffect(() => {
    dispatch(fetchSystemHealth());
    if (pollInterval > 0) {
      intervalRef.current = setInterval(() => {
        dispatch(fetchSystemHealth({ force: true }));
      }, pollInterval);
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }
  }, [dispatch, pollInterval]);

  const modules = health?.modules ?? {};
  const moduleKeys = Object.keys(modules);

  // ── Compact variant ──────────────────────────────────────────────

  if (variant === 'compact') {
    if (loading && !health) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CircularProgress size={10} thickness={5} />
          <Typography variant="caption" color="text.secondary">Checking services…</Typography>
        </Box>
      );
    }

    if (error && !health) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <FiberManualRecordIcon color="error" sx={{ fontSize: 10 }} />
          <Typography variant="caption" color="error">Unable to reach server</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {/* Overall status line */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
          <StatusIcon status={overall} size={12} />
          <Typography variant="caption" fontWeight={typography.label.fontWeight} textTransform="uppercase" letterSpacing={0.5}>
            {statusLabel(overall)}
          </Typography>
          {loading && <CircularProgress size={8} thickness={5} sx={{ ml: 0.5 }} />}
        </Box>
        {/* Module dots */}
        {moduleKeys.map((key) => (
          <ModuleDot key={key} moduleKey={key} module={modules[key]} />
        ))}
      </Box>
    );
  }

  // ── Expanded variant ─────────────────────────────────────────────

  return (
    <Box>
      {/* Overall summary bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
          p: 1.5,
          borderRadius: 1,
          bgcolor: overall === 'healthy'
            ? 'rgba(46,125,50,0.06)'
            : overall === 'degraded'
              ? 'rgba(237,108,2,0.06)'
              : overall === 'down'
                ? 'rgba(211,47,47,0.06)'
                : 'action.hover',
        }}
      >
        <StatusIcon status={overall} size={20} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={typography.label.fontWeight}>
            System Status: {statusLabel(overall)}
          </Typography>
          {health?.timestamp && (
            <Typography variant="caption" color="text.secondary">
              Last checked: {new Date(health.timestamp).toLocaleTimeString()}
            </Typography>
          )}
        </Box>
        {loading && <CircularProgress size={16} />}
        <Chip
          label={`${moduleKeys.length} modules`}
          size="small"
          variant="outlined"
          sx={{ height: 20, fontSize: typography.caption.fontSize }}
        />
      </Box>

      {/* Warnings */}
      {health?.warnings && health.warnings.length > 0 && (
        <Box sx={{ mb: 1.5, p: 1, bgcolor: 'rgba(237,108,2,0.06)', borderRadius: 1 }}>
          {health.warnings.map((w, i) => (
            <Typography key={i} variant="caption" color="warning.main" display="block">
              ⚠ {w}
            </Typography>
          ))}
        </Box>
      )}

      {/* Module sections */}
      {moduleKeys.map((key) => (
        <ModuleSection key={key} moduleKey={key} module={modules[key]} />
      ))}

      {error && !health && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          Failed to load system health: {error}
        </Typography>
      )}
    </Box>
  );
};

export default SystemStatusIndicator;
